import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import { isLearningJourneyOperation } from "@repo/app-types/helpers/configurationAgentParser";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { messageItemStyle } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import * as Sentry from "@sentry/react";
import { IconSparkles } from "@tabler/icons-react";
import { useEffect, useMemo, useRef, type FC, type ReactNode } from "react";
import { buildInteractionResolverFromMessages } from "../interactionResolver.js";
import { useMastraChatContext } from "../mastra/mastraChatContext.js";
import { FallbackTool } from "../tools/fallbackTool.js";
import { GetAgentInstructionChecksTool } from "../tools/getAgentInstructionChecksTool.js";
import { GetCriterionDetailsTool } from "../tools/getCriterionDetailsTool.js";
import { GetKnowledgeDocumentTool } from "../tools/getKnowledgeDocumentTool.js";
import { ListCustomerInteractionsTool } from "../tools/listCustomerInteractionsTool.js";
import { SimulateAgentConversationTool } from "../tools/simulateAgentConversationTool.js";
import { SimulateRolePlayRealismTool } from "../tools/simulateRolePlayRealismTool.js";
import { WebSearchTool } from "../tools/webSearchTool.js";
import { ConfigurationAgentMessageContent } from "./configurationAgentMessageContent.js";
import {
  buildPrefillMessage,
  usePrefillFromUrl,
} from "./configurationAssistantPrefill.js";
import { InlineChangeCard } from "./inlineChangeCard.js";
import { LearningJourneyOperationCard } from "./learningJourneyOperationCard.js";

export interface ConfigurationAssistantChatProps {
  title: string;
  description: string;
  placeholder: string;
  suggestions: readonly string[];
  /** Optional content rendered just above the composer (e.g. a suggestion list). */
  aboveComposer?: ReactNode;
}

const TOOL_COMPONENTS = {
  byName: {
    getCriterionDetailsTool: GetCriterionDetailsTool,
    listCustomerInteractionsTool: ListCustomerInteractionsTool,
    getKnowledgeDocumentTool: GetKnowledgeDocumentTool,
    // Pre-rename key, kept so historical threads render.
    knowledgeRetrievalTool: GetKnowledgeDocumentTool,
    simulateAgentConversationTool: SimulateAgentConversationTool,
    simulateRolePlayRealismTool: SimulateRolePlayRealismTool,
    getAgentInstructionChecksTool: GetAgentInstructionChecksTool,
    webSearch: WebSearchTool,
  },
  Fallback: FallbackTool,
};

export const ConfigurationAssistantChat: FC<
  ConfigurationAssistantChatProps
> = ({ title, description, placeholder, suggestions, aboveComposer }) => {
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const chat = useMastraChatContext();

  const prefill = usePrefillFromUrl();
  const hasSentPrefillRef = useRef(false);

  // Auto-send the initial message when prefill data is provided.
  useEffect(() => {
    if (prefill && !hasSentPrefillRef.current) {
      hasSentPrefillRef.current = true;
      chat.sendMessage(buildPrefillMessage(prefill));
    }
  }, [prefill, chat]);

  // Quotes and contact references in the agent's messages cite interactions
  // returned by its tool calls, so resolve them against those tool results.
  const resolveInteractionId = useMemo(
    () => buildInteractionResolverFromMessages(chat.messages),
    [chat.messages],
  );

  const lastMessageId = chat.messages.at(-1)?.id;

  return (
    <ChatThread
      className="flex-1"
      messages={chat.messages}
      isLoading={chat.isLoading}
      emptyState={
        <EmptyState
          title={title}
          description={description}
          suggestions={suggestions}
          onSuggestionClick={chat.sendMessage}
        />
      }
      renderMessage={(message) => (
        <Sentry.ErrorBoundary fallback={<AssistantMessageError />}>
          <ChatMessage
            message={message}
            isStreaming={chat.isRunning && message.id === lastMessageId}
            tools={TOOL_COMPONENTS}
            renderText={(args) =>
              renderConfigurationText(args, resolveInteractionId)
            }
          />
        </Sentry.ErrorBoundary>
      )}
      composer={
        <>
          {/* A detached card above the composer box (not inside it), mirroring
              the playbook editor's suggestion list. */}
          {aboveComposer}
          <div className="border-t border-border-light bg-white p-3">
            <ChatComposer
              inputRef={composerInputRef}
              size={Size.SMALL}
              placeholder={placeholder}
              isRunning={chat.isRunning}
              onStop={chat.stop}
              onSend={chat.sendMessage}
            />
          </div>
        </>
      }
    />
  );
};

/** Renders a configuration-agent text part, interleaving operation cards. */
function renderConfigurationText(
  { text, role, messageId, isStreaming }: ChatMessageRenderTextArgs,
  resolveInteractionId: InteractionResolver,
) {
  return (
    <ConfigurationAgentMessageContent
      text={text}
      role={role}
      isStreaming={isStreaming}
      resolveInteractionId={resolveInteractionId}
      renderOperation={(segment) => {
        return isLearningJourneyOperation(segment.operation) ? (
          <LearningJourneyOperationCard
            operationId={`${messageId}-${segment.id}`}
            operation={segment.operation}
          />
        ) : (
          <InlineChangeCard
            operationId={`${messageId}-${segment.id}`}
            operation={segment.operation}
          />
        );
      }}
    />
  );
}

const EmptyState: FC<{
  title: string;
  description: string;
  suggestions: readonly string[];
  onSuggestionClick: (suggestion: string) => void;
}> = ({ title, description, suggestions, onSuggestionClick }) => {
  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="flex size-10 items-center justify-center rounded-full bg-indigo-100">
          <IconSparkles className="size-5 text-indigo-600" />
        </div>

        <h3 className="mt-3 text-sm font-semibold text-foreground">{title}</h3>
        <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">
          {description}
        </p>
      </div>

      <div className="flex flex-wrap gap-2 pb-0">
        {suggestions.map((suggestion) => (
          <SuggestionPill
            key={suggestion}
            onClick={() => onSuggestionClick(suggestion)}
          >
            {suggestion}
          </SuggestionPill>
        ))}
      </div>
    </div>
  );
};

const SuggestionPill: FC<{ children: string; onClick: () => void }> = ({
  children,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="rounded-full border border-border-light bg-white px-3 py-1.5 text-xs text-gray-700 shadow-xs transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
  >
    {children}
  </button>
);

const AssistantMessageError: FC = () => (
  <div
    className={messageItemStyle({ layout: "agent", role: "assistant" }).bubble({
      className: "bg-red-100 px-3.5",
    })}
  >
    Unable to render this message.
  </div>
);
